import "@/App.css";
import { useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import HomePage from "@/pages/HomePage";
import ProgramPage from "@/pages/ProgramPage";

const PRIMARY_ORIGIN = "https://shyrabhalla.com";
const PRIMARY_HOST = "shyrabhalla.com";
const WWW_HOST = "www.shyrabhalla.com";

const ensureMetaTag = (name) => {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  return tag;
};

const ensurePropertyMetaTag = (property) => {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  return tag;
};

const ensureCanonicalTag = () => {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  return link;
};

const normalizeCanonicalPath = (pathname) => {
  if (!pathname || pathname === "/") return "/";
  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const targetId = hash.replace("#", "");

      const scrollToHashElement = () => {
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: "auto", block: "start" });
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
      };

      window.setTimeout(scrollToHashElement, 60);
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
};

const SeoAndDomainManager = () => {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    const host = window.location.hostname.toLowerCase();
    const isPrimaryHost = host === PRIMARY_HOST;
    const isWwwHost = host === WWW_HOST;
    const isEmergentPreview =
      host.includes("emergentagent.com") || host.includes("emergent.host");

    if (isWwwHost || (window.location.protocol === "http:" && (isPrimaryHost || isWwwHost))) {
      const redirectTarget = `${PRIMARY_ORIGIN}${pathname}${search}${hash}`;
      if (window.location.href !== redirectTarget) {
        window.location.replace(redirectTarget);
        return;
      }
    }

    const canonicalPath = normalizeCanonicalPath(pathname);

    if (pathname !== canonicalPath) {
      window.history.replaceState(null, "", `${canonicalPath}${search}${hash}`);
    }

    const canonicalUrl = `${PRIMARY_ORIGIN}${canonicalPath}`;

    document.title = "Shyra Bhalla | Nutrition & Weight Management";

    const description =
      "Functional nutrition and wellness coaching for weight management, gut health, and hormonal balance. Transform your health with science-backed, sustainable strategies.";

    const descriptionTag = ensureMetaTag("description");
    descriptionTag.setAttribute("content", description);

    const robotsTag = ensureMetaTag("robots");
    robotsTag.setAttribute(
      "content",
      isEmergentPreview
        ? "noindex, nofollow, noarchive"
        : "index, follow",
    );

    const canonicalTag = ensureCanonicalTag();
    canonicalTag.setAttribute("href", canonicalUrl);

    const ogUrlTag = ensurePropertyMetaTag("og:url");
    ogUrlTag.setAttribute("content", canonicalUrl);

    const iconLinks = [
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-32x32.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/favicon-16x16.png",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
    ];

    iconLinks.forEach((icon) => {
      const selector = `link[rel="${icon.rel}"]${icon.sizes ? `[sizes="${icon.sizes}"]` : ""}`;
      let linkTag = document.querySelector(selector);
      if (!linkTag) {
        linkTag = document.createElement("link");
        linkTag.setAttribute("rel", icon.rel);
        document.head.appendChild(linkTag);
      }

      if (icon.type) linkTag.setAttribute("type", icon.type);
      if (icon.sizes) linkTag.setAttribute("sizes", icon.sizes);
      linkTag.setAttribute("href", icon.href);
    });
  }, [pathname, search, hash]);

  return null;
};

function App() {
  useEffect(() => {
    const removeEmergentBadge = () => {
      const badgeById = document.getElementById("emergent-badge");
      if (badgeById) {
        badgeById.remove();
      }

      const badgeByLink = document.querySelector(
        'a[href*="emergent-badge"], a[href*="utm_source=emergent-badge"]',
      );
      if (badgeByLink) {
        badgeByLink.remove();
      }
    };

    const timers = [0, 300, 1200, 2400].map((delay) =>
      window.setTimeout(() => {
        removeEmergentBadge();
      }, delay),
    );

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  return (
    <BrowserRouter>
      <SeoAndDomainManager />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/the-program" element={<ProgramPage />} />
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/index.html" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
